import type { SingleTrackPeaksDto } from '@mimir/common/dto/peaksDto';
import type { PeakRange } from './audioTrackConfig';
import { getPeaksFromUint8Array } from '../components/Common/Timeline/Audio/audioUtils';
import type { ObservableState } from '@mimir/common/util/observable';
import { invariant } from '@mimir/common/util/invariant';
import type { BasicFetch } from '@mimir/common/types/basicFetch';
import { fetchOrThrow } from '@mimir/common/io/fetchOrThrow';
import { readWholeStream } from '@mimir/common/util/readableStream';
import type { Logger } from '@mimir/common/types/logger';
import { decodeBase64Sync } from '../browser/base64';

export class PeaksManager {

  private peaks: Uint8Array[];
  private initializing = false;
  private nPeaksPerSecond: number | null = null;
  private maybeMoreContent = true;

  constructor(
    private logger: Logger,
    private audioTrackCount: number,
    private fetchPeaks: (audioTrackIndex: number) => Promise<SingleTrackPeaksDto>,
    private peakCount: ObservableState<number>,
    private fetch: BasicFetch,
  ) {
    peakCount.value = 0;
    this.peaks = [];
    for (let i = 0; i < audioTrackCount; i++) {
      this.peaks.push(new Uint8Array(0));
    }
  }


  /**
   * Loads the peaks currently available from the beginning of the media.
   */
  async initializePeaks(
    nPeaksPerSecondPerChannel: number | null,
    peaksUrls: string[] | null,
  ) {
    if (this.initializing || this.audioTrackCount === 0) {
      return;
    }

    this.initializing = true;
    try {
      let peakArrays: Uint8Array[];
      if (nPeaksPerSecondPerChannel !== null && peaksUrls !== null) {
        // If the peaks are already in S3, let's fetch them from there, since it
        // is more efficient and more cache friendly than the lambda
        peakArrays = await this.loadPeaksFromS3(nPeaksPerSecondPerChannel, peaksUrls);
      } else {
        peakArrays = await this.fetchPeaksFromStart();
      }
      this.updatePeaksInternal(peakArrays);
    } finally {
      this.initializing = false;
    }
  }


  private findMinimumSize(peakArrays: Uint8Array[]): number {
    if (peakArrays.length === 0) {
      return 0;
    }

    let min = peakArrays[0].length;
    for (let i = 1; i < peakArrays.length; i++) {
      if (peakArrays[i].length < min) {
        min = peakArrays[i].length;
        // Since we will truncate some of the data,
        // let's note that there is more content to be fetched
        this.maybeMoreContent = true;
      }
    }

    return min;
  }

  /**
   * Adds peaks and returns the number of peaks added.
   */
  public addPeaksDelta(peakOffset: number, base64PeakDeltaArrays: string[]): number {
    if (peakOffset !== this.peakCount.value) {
      console.warn(`Discarding peak delta update: expected offset ${this.peakCount.value}, got ${peakOffset}`);
      return 0;
    }

    // When using flac downmix, there should not be any mismatch
    invariant(base64PeakDeltaArrays.length === this.audioTrackCount, `Expected ${this.audioTrackCount} peak arrays but got ${base64PeakDeltaArrays.length}`);

    const peakCountBefore = this.peakCount.value;
    this.updatePeaksInternal(base64PeakDeltaArrays.map(base64Data => decodeBase64Sync(base64Data)));
    const peakCountAfter = this.peakCount.value;
    return peakCountAfter - peakCountBefore;
  }


  private updatePeaksInternal(peakArrays: Uint8Array[]) {
    const currentPeakCount = this.peakCount.value;

    // To avoid complication, we want to have all peaks arrays
    // to grow at the same pace. However, since we fetch the peaks
    // of the audio tracks individually, it might be that not all
    // peaks arrays have exactly the same size. As a consequence, we
    // find the smallest array and use this minimum size to truncate
    // larger arrays
    const minimumSize = this.findMinimumSize(peakArrays);
    const newPeakCount = currentPeakCount + minimumSize;

    for (let i = 0; i < this.audioTrackCount; i++) {
      const old = this.peaks[i];
      const bytes = peakArrays[i];
      this.peaks[i] = new Uint8Array(newPeakCount);
      this.peaks[i].set(old, 0);
      this.peaks[i].set(bytes.slice(0, minimumSize), old.length);
    }

    this.peakCount.value = newPeakCount;
  }


  /**
   * Fetches all the content available from the start of the media.
   *
   * Since the binary data is base64 encoded which incurs a 33% size
   * cost, a single fetch per audio track will fit inside the 6Mb lambda
   * response limit until we reach recording longer than 22 hours.
   * So let's keep it simple until we actually need to address this limit.
   */
  private async fetchPeaksFromStart(): Promise<Uint8Array[]> {
    const peakArrays: Uint8Array[] = [];
    const promises: Promise<SingleTrackPeaksDto>[] = [];
    for (let i = 0; i < this.audioTrackCount; i++) {
      promises.push(this.fetchPeaks(i));
    }
    const dtos = await Promise.all(promises);
    let maybeMore = false;
    for (let i = 0; i < this.audioTrackCount; i++) {
      if (this.nPeaksPerSecond === null) {
        this.nPeaksPerSecond = dtos[i].peaksPerSeconds;
      }

      // If any of the tracks may have more content, we want to
      if (dtos[i].maybeMoreContent) {
        maybeMore = dtos[i].maybeMoreContent;
      }
      const bytes = decodeBase64Sync(dtos[i].base64Data);
      peakArrays.push(bytes);
    }

    this.maybeMoreContent = maybeMore;
    return peakArrays;
  }


  private async loadPeaksFromS3(
    nPeaksPerSecondPerChannel: number,
    peaksUrls: string[],
  ): Promise<Uint8Array[]> {
    const promises: Promise<Uint8Array>[] = [];

    for (let i = 0; i < peaksUrls.length; i++) {
      promises.push(this.fetchBytes(peaksUrls[i]));
    }

    const peakArrays: Uint8Array[] = await Promise.all(promises);
    this.nPeaksPerSecond = nPeaksPerSecondPerChannel;
    this.maybeMoreContent = false;
    return peakArrays;
  }

  private async fetchBytes(url: string): Promise<Uint8Array> {
    const response = await fetchOrThrow(this.logger, this.fetch, url);
    const data = response.body ? await readWholeStream(response.body) : null;
    if (!data) {
      throw new Error(`Audio channel: ${url} failed to download`);
    }
    return data;
  }


  /**
   * We have one peaks series per audio track since all channels are
   * processed individually.
   */
  public getPeakSeriesCount(_streamIndex: number): number {
    return 1;
  }


  public getPeaks(
    audioTrackIndex: number,
    range: PeakRange,
    width: number,
  ): number[] {
    const peakBuffer = this.peaks[audioTrackIndex];
    return getPeaksFromUint8Array(peakBuffer, this.nPeaksPerSecond, range, width);
  }


  /**
   * Returns the offset position to fetch more peaks from or null if no
   * incremental fetching should be performed.
   */
  public getOffsetToFetchFrom(): number | undefined {
    // If we know that we have all the content or if we have not fetched the content yet,
    // we return undefined to indicate that no incremental fetching should be done
    if (!this.maybeMoreContent || this.peakCount.value === 0) {
      return undefined;
    }

    return this.peakCount.value;
  }

}
